import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { CalendarClock, CheckCircle2, Loader2, RefreshCw, XCircle } from 'lucide-react';
import { api, mensajeError } from '../lib/api';
import { Button } from '../components/ui/button';
import {
  useTelefonoClinica, tipoDeFallo, respuestaPerdida, cuandoReintentar, aQuienLlamar, conMayuscula,
} from '../lib/enlacePaciente';

/**
 * Página PÚBLICA (sin login) del enlace de recordatorio: el paciente
 * confirma o cancela su cita desde el móvil. Phase 27.17.
 *
 * Es la ÚNICA pantalla de ClinicPilot que ve una paciente, y no tiene a nadie
 * al lado para explicarle lo que pone. Por eso aquí se cuida más que en
 * ninguna otra que lo que se lee sea verdad (revisión 2026-09-21):
 *
 *  - Un fallo del servidor NO es un enlace muerto. Antes cualquier rechazo
 *    —un 500, un timeout, un corte de red— se le contaba como «Este enlace no
 *    es válido o ha caducado»: la paciente no confirmaba, no cancelaba y no
 *    llamaba, y la clínica lo apuntaba como «no ha confirmado». Ahora se mira
 *    el status y se le dice lo que de verdad pasa, con un botón de reintentar
 *    y el teléfono de la clínica cuando el fallo puede pasarse solo.
 *  - Y «o ha caducado» se ha caído del texto: el enlace de confirmar cita NO
 *    caduca (no hay vencimiento en `_cita_por_token`), así que esa mitad de
 *    la frase no podía ser cierta jamás.
 *  - La cita anulada manda sobre lo que la paciente respondiera antes: si
 *    confirmó y DESPUÉS la clínica la anuló, el enlace no puede seguir
 *    diciéndole que su cita está confirmada (se presentaría en la clínica con
 *    el móvil dándole la razón).
 *
 * Sin rotura de conexión (23/9/2026, dirección):
 *  - Un 422 (token cortado al copiarlo) contestaba «Es cosa nuestra, no de
 *    su enlace»: es un enlace INCOMPLETO y se dice así. Y sin respuesta del
 *    servidor tampoco se sabe si es cosa nuestra o de su cobertura.
 *  - Todos los avisos llevan el teléfono (el de la cita si se cargó; si no,
 *    el de Configuración o el de respaldo: `useTelefonoClinica`).
 *  - La cita de AYER venía con sus dos botones y al pulsar el servidor
 *    contestaba «La cita ya ha pasado». Ahora el servidor dice `ya_paso` con
 *    la MISMA regla que usa al responder, y aquí se enseña cuándo fue y a
 *    quién llamar, sin botones.
 *  - Una respuesta que se guardó y cuya contestación se perdió no acaba en
 *    «No se pudo registrar»: se pregunta al servidor antes de decir nada.
 *
 * Segunda pasada del 23/9/2026:
 *  - `?t=` vacío (o sin `t`) es el enlace cortado al copiarlo: «enlace
 *    incompleto», como un 422, no «no válido».
 *  - Sin teléfono en ningún sitio no se inventa uno: «llame a la clínica»
 *    (`aQuienLlamar`). Antes caía en el de CJO, y este build sirve a otras.
 *  - Una cita sin fecha legible daba 500. Ahora el servidor la manda con
 *    `sin_fecha` y aquí se enseña sin fecha, con el teléfono y SIN botones:
 *    confirmar una cita que no sabemos cuándo es sería confirmar a ciegas.
 */

/** Marca de «no hay token en la URL»: no se pregunta nada al servidor. */
const SIN_TOKEN = { sinToken: true };

/**
 * Qué se le dice a la paciente según lo que ha pasado DE VERDAD al pedir su
 * cita, y si tiene sentido que lo vuelva a intentar. Se calcula al pintar,
 * con el teléfono de ese momento (el de Configuración puede llegar después).
 *
 *  - Sin token: el enlace se cortó al copiarlo (incompleto); que abra el
 *    del mensaje.
 *  - 404: ese enlace no existe. Reintentar no lo va a arreglar.
 *  - 410: la cita está anulada, en «no acude» o es de antes de ayer (M30,
 *    22/9/2026). El servidor ya no manda nombre, fecha ni tipo, solo el texto
 *    que hay que leer, con el teléfono dentro; se enseña tal cual y sin
 *    botones, porque ya no hay nada que confirmar ni que anular.
 *  - 422: el token no llega a la longitud mínima: se cortó al copiarlo. Es un
 *    enlace INCOMPLETO, no una avería nuestra (23/9/2026).
 *  - 429: el limitador público (`_public_rate_limit`) la ha frenado. Ojo con
 *    el texto: no puede contener «no es válido»; y dice cuánto esperar.
 *  - Sin respuesta (red, timeout): no sabemos de quién es el corte; que
 *    compruebe su conexión y reintente.
 *  - 5xx: es nuestro, no suyo. Se lo decimos así.
 */
function falloDeCarga(e, telefono) {
  const llamenos = aQuienLlamar(telefono, { usted: true });
  if (e?.sinToken) {
    return {
      texto: 'Este enlace está incompleto',
      ayuda: `Abra el enlace completo que le hemos enviado. Si no lo encuentra, ${llamenos}.`,
      reintentar: false,
    };
  }
  const status = e?.response?.status;
  if (status === 410) {
    const detalle = e?.response?.data?.detail;
    if (typeof detalle === 'string' && detalle.trim()) {
      return { texto: detalle.trim(), ayuda: '', reintentar: false };
    }
    return {
      texto: 'Esta cita ya no está activa.',
      ayuda: `${conMayuscula(llamenos)} y le damos una nueva fecha.`,
      reintentar: false,
    };
  }
  const tipo = tipoDeFallo(e);
  if (tipo === 'invalido') {
    return {
      texto: 'Este enlace no es válido',
      ayuda: `Compruebe que ha abierto el enlace completo del mensaje. Si el problema sigue, ${llamenos}.`,
      reintentar: false,
    };
  }
  if (tipo === 'incompleto') {
    return {
      texto: 'Este enlace está incompleto',
      ayuda: `Ábralo desde el mensaje completo que le enviamos. Si no lo encuentra, ${llamenos}.`,
      reintentar: false,
    };
  }
  if (tipo === 'espera') {
    return {
      texto: 'Inténtelo dentro de un rato, por favor',
      ayuda: `Hay mucho movimiento en este momento. Podrá volver a entrar ${cuandoReintentar(e)}.`,
      reintentar: true,
    };
  }
  if (!e?.response) {
    return {
      texto: 'No hemos podido cargar su cita ahora mismo',
      ayuda: 'No hemos podido conectar con la clínica. Compruebe su conexión y vuelva a intentarlo: su enlace sigue siendo válido.',
      reintentar: true,
    };
  }
  return {
    texto: 'No hemos podido cargar su cita ahora mismo',
    ayuda: 'Es cosa nuestra, no de su enlace. Vuelva a intentarlo en un momento.',
    reintentar: true,
  };
}

export default function ConfirmarCitaPage() {
  const [params] = useSearchParams();
  const token = params.get('t') || '';
  const [cita, setCita] = useState(null);
  // `fallo` = no se ha podido cargar la cita: el error tal cual (o SIN_TOKEN);
  // lo que se le dice sale de `falloDeCarga` al pintar.
  // `error` = la cita se cargó pero la respuesta no se pudo registrar (o no
  // se sabe si llegó): {texto, ayuda, reintentar}.
  const [fallo, setFallo] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [hecho, setHecho] = useState(null); // 'confirmada' | 'cancelada'
  const [preguntaCancelar, setPreguntaCancelar] = useState(false);

  const cargar = useCallback(() => {
    if (!token) {
      setFallo(SIN_TOKEN);
      setCargando(false);
      return;
    }
    setCargando(true);
    api.get(`/api/public/confirmar-cita/${token}`)
      .then((res) => {
        setFallo(null);
        setCita(res.data);
        if (res.data.respuesta) setHecho(res.data.respuesta);
      })
      .catch((e) => setFallo(e))
      .finally(() => setCargando(false));
  }, [token]);

  useEffect(() => { cargar(); }, [cargar]);

  /**
   * La respuesta se envió pero la contestación no llegó (sin respuesta o un
   * 5xx del proxy): pudo guardarse. Antes de decir nada se pregunta por la
   * cita (23/9/2026): si ya consta lo que respondió, es lo que se enseña; si
   * la cita ya está cerrada (410: la anuló ella misma, o la clínica), el
   * texto del servidor; si consta sin respuesta, que no llegó; y si ni eso se
   * puede preguntar, que no se sabe.
   */
  const comprobarRespuesta = async (esperada) => {
    try {
      const { data } = await api.get(`/api/public/confirmar-cita/${token}`);
      setCita(data);
      if (data?.respuesta === esperada) {
        setHecho(esperada);
        return;
      }
      setError({
        texto: 'Su respuesta no ha llegado a la clínica',
        ayuda: 'Compruebe su conexión y vuelva a intentarlo.',
        reintentar: true,
      });
    } catch (e) {
      if (e?.response?.status === 410) {
        setFallo(e);
        return;
      }
      setError({
        texto: 'No hemos podido comprobar si su respuesta ha llegado',
        ayuda: 'Compruebe su conexión y vuelva a intentarlo: si ya había llegado, se lo diremos.',
        reintentar: true,
      });
    }
  };

  const responder = async (accion) => {
    setEnviando(true);
    try {
      // Con la fecha y la hora que está viendo (30/9/2026): si la clínica
      // movió la cita con la página abierta, el servidor no la da por
      // respondida (`cambiada`) y «Reintentar» la vuelve a cargar con la
      // fecha de ahora. La respuesta es de la cita que ve, no de otra.
      const res = await api.post(`/api/public/confirmar-cita/${token}`, {
        accion, fecha: cita?.fecha ?? null, hora: cita?.hora ?? null,
      });
      if (res.data.ok) setHecho(res.data.respuesta);
      else {
        setError({
          texto: res.data.motivo || 'No se pudo registrar la respuesta',
          ayuda: '',
          reintentar: !!res.data.cambiada,
        });
      }
    } catch (e) {
      if (respuestaPerdida(e)) {
        await comprobarRespuesta(accion === 'confirmar' ? 'confirmada' : 'cancelada');
      } else if (e?.response?.status === 429) {
        setError({
          texto: 'Hay mucho movimiento en este momento',
          ayuda: `Vuelva a intentarlo ${cuandoReintentar(e)}.`,
          reintentar: true,
        });
      } else {
        // Si el servidor explica el motivo (enlace caducado, cita ya anulada…)
        // se enseña; si no, el texto de siempre (revisión de enlaces 2026-09-07).
        setError({
          texto: mensajeError(e, 'No se pudo registrar la respuesta. Llame a la clínica, por favor.'),
          ayuda: '',
          reintentar: false,
        });
      }
    } finally {
      setEnviando(false);
    }
  };

  /** Vuelve a pedir la cita: enseña lo que hay DE VERDAD (su respuesta si
   *  llegó; los botones si no). */
  const reintentarRespuesta = () => {
    setError(null);
    setPreguntaCancelar(false);
    cargar();
  };

  // El de la cita si se cargó; si no, el de Configuración o el último que
  // este dispositivo leyó bien; y si no hay ninguno, vacío (sin inventarlo).
  const telefono = useTelefonoClinica(cita?.telefono_clinica, !cargando && !cita);
  const llamenos = aQuienLlamar(telefono, { usted: true });
  const textoFallo = fallo ? falloDeCarga(fallo, telefono) : null;
  // Cita de un día anterior a hoy (la de ayer aún se enseña): ni botones ni
  // «le esperamos». La regla y el texto son los del servidor (`ya_paso`).
  const yaPaso = !!cita?.ya_paso;
  // Cita sin fecha legible (23/9/2026): se enseña sin fecha, con el
  // teléfono y sin botones. Tampoco se pinta «null» si el servidor no la da.
  const sinFecha = !!cita?.sin_fecha || (!!cita && !cita.fecha_texto);
  // El estado de la cita EN LA CLÍNICA manda sobre lo que respondiera la
  // paciente: si está anulada, ya no está confirmada, dijera lo que dijera.
  const activa = cita?.estado_cita === 'activa';
  // Salvo que la anulara ella misma: entonces lo que tiene que leer es que su
  // cancelación quedó hecha, no un «ya no está activa» que no explica nada.
  const anuladaPorElla = hecho === 'cancelada';

  return (
    <div className="min-h-screen bg-[#f5f7fa] flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-xl shadow-lg border border-[#D9E3DD] p-6 space-y-5">
        {cargando ? (
          <div className="flex items-center justify-center gap-2 py-10 text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin" /> Cargando su cita…
          </div>
        ) : textoFallo ? (
          <div className="text-center py-8 space-y-3" data-testid="confirmar-cita-fallo">
            <XCircle className="w-10 h-10 text-red-500 mx-auto" />
            <p className="text-[#13231E] font-medium">{textoFallo.texto}</p>
            {textoFallo.ayuda && <p className="text-sm text-muted-foreground">{textoFallo.ayuda}</p>}
            {textoFallo.reintentar && (
              <>
                <Button
                  variant="outline"
                  className="h-11"
                  onClick={cargar}
                  data-testid="confirmar-cita-reintentar"
                >
                  <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
                </Button>
                {/* Lo único que puede hacer ahora mismo si no carga: llamar. */}
                <p className="text-sm text-muted-foreground">
                  Si sigue sin cargar, {llamenos}.
                </p>
              </>
            )}
          </div>
        ) : error ? (
          <div className="text-center py-8 space-y-3" data-testid="confirmar-cita-error">
            <XCircle className="w-10 h-10 text-red-500 mx-auto" />
            <p className="text-[#13231E] font-medium">{error.texto}</p>
            {error.ayuda && <p className="text-sm text-muted-foreground">{error.ayuda}</p>}
            {error.reintentar && (
              <Button
                variant="outline"
                className="h-11"
                onClick={reintentarRespuesta}
                data-testid="confirmar-cita-reintentar"
              >
                <RefreshCw className="w-4 h-4 mr-2" /> Reintentar
              </Button>
            )}
            {/* El teléfono, salvo que el texto del servidor ya lo lleve. Sin
                número, `includes('')` es siempre cierto y la línea se perdía:
                entonces se dice «llame a la clínica» (23/9/2026). */}
            {!(telefono && error.texto.includes(telefono)) && (
              <p className="text-sm text-muted-foreground">
                {error.reintentar ? `Si sigue sin funcionar, ${llamenos}.` : `${conMayuscula(llamenos)}, por favor.`}
              </p>
            )}
          </div>
        ) : (
          <>
            <div className="text-center space-y-1">
              <div className="font-display text-xl font-semibold text-[#13231E]">{cita.clinica}</div>
              <p className="text-sm text-muted-foreground">Recordatorio de cita</p>
            </div>

            <div className="rounded-lg bg-[rgba(46,110,94,0.07)] border border-[#D9E3DD] p-4 text-center space-y-1">
              {/* Cita online: no hay dirección a la que ir (el backend la
                  manda vacía) y no se «espera» a nadie: la frase es la de
                  la videollamada, como en el WhatsApp (cierre 2026-09-05). */}
              {/* Primero lo que manda sobre todo (23/9/2026): sin fecha no
                  hay «hora indicada» y una cita que ya pasó no «le
                  contactará»; una online de ayer decía las dos cosas a la vez. */}
              {sinFecha ? (
                <p className="text-sm text-[#13231E]">Hola {cita.nombre}, tiene una cita con nosotros:</p>
              ) : yaPaso ? (
                <p className="text-sm text-[#13231E]">Hola {cita.nombre}, su cita fue el</p>
              ) : cita.online ? (
                <p className="text-sm text-[#13231E]" data-testid="confirmar-cita-online">
                  Hola {cita.nombre}, el doctor le contactará por <em>videollamada de WhatsApp</em> a la hora indicada:
                </p>
              ) : (
                <p className="text-sm text-[#13231E]">Hola {cita.nombre}, le esperamos el</p>
              )}
              {/* La misma fecha que el WhatsApp: «Jueves, 10 de septiembre · 17:30 h»
                  (el texto de la fecha lo da ya el backend, con el año solo si cambia).
                  Sin fecha legible no se pinta ni una fecha ni una hora
                  sueltas: se dice que está por confirmar (23/9/2026). */}
              <div className="flex items-center justify-center gap-2 text-lg font-semibold text-[#13231E]" data-testid="confirmar-cita-fecha">
                <CalendarClock className="w-5 h-5 text-[#2E6E5E]" />
                {sinFecha ? 'Fecha por confirmar' : `${cita.fecha_texto} · ${cita.hora} h`}
              </div>
              <p className="text-sm text-muted-foreground">{cita.tipo}</p>
              {cita.direccion_clinica && (
                <p className="text-xs text-muted-foreground pt-1">{cita.direccion_clinica}</p>
              )}
            </div>

            {/* ORDEN (revisión 2026-09-21): primero lo que la paciente hizo
                ella misma —cancelar—, después el estado de la cita en la
                clínica, y solo al final «confirmada». Al revés, una cita
                anulada por la clínica después de que la paciente confirmara
                seguía enseñándole «Su cita queda confirmada». */}
            {anuladaPorElla ? (
              <div className="text-center space-y-2 py-2">
                <XCircle className="w-10 h-10 text-red-500 mx-auto" />
                <p className="font-medium text-[#13231E]">Su cita ha quedado anulada.</p>
                <p className="text-sm text-muted-foreground">
                  Si desea una nueva fecha, {llamenos}.
                </p>
              </div>
            ) : !activa ? (
              <div className="text-center space-y-2 py-2" data-testid="confirmar-cita-no-activa">
                <XCircle className="w-10 h-10 text-red-500 mx-auto" />
                <p className="font-medium text-[#13231E]">Esta cita ya no está activa.</p>
                <p className="text-sm text-muted-foreground">
                  {conMayuscula(llamenos)} y le damos una nueva fecha.
                </p>
              </div>
            ) : yaPaso ? (
              /* Ya pasó (23/9/2026): lo que confirmara o no ya no importa,
                 y los botones no se enseñan porque el servidor rechazaría la
                 respuesta. Cuándo fue y a quién llamar si necesita otra. */
              <div className="text-center space-y-2 py-2" data-testid="confirmar-cita-ya-paso">
                <CalendarClock className="w-10 h-10 text-[#2E6E5E] mx-auto" />
                <p className="text-sm text-[#13231E]">
                  {cita.texto_ya_paso
                    || `Su cita fue el ${String(cita.fecha_texto || '').charAt(0).toLowerCase()}${String(cita.fecha_texto || '').slice(1)}. Si necesita otra, ${llamenos}.`}
                </p>
              </div>
            ) : sinFecha ? (
              /* Sin fecha legible (23/9/2026): el servidor daba 500. Ni botones
                 (confirmar una cita que no sabemos cuándo es sería a ciegas; el
                 servidor tampoco lo acepta) ni «queda confirmada» sin fecha:
                 qué pasa y a quién llamar. */
              <div className="text-center space-y-2 py-2" data-testid="confirmar-cita-sin-fecha">
                <CalendarClock className="w-10 h-10 text-[#2E6E5E] mx-auto" />
                <p className="text-sm text-[#13231E]">
                  {cita.texto_sin_fecha
                    || `No podemos enseñarle aquí la fecha de su cita. ${conMayuscula(llamenos)} y se la confirmamos.`}
                </p>
              </div>
            ) : hecho === 'confirmada' ? (
              <div className="text-center space-y-2 py-2">
                <CheckCircle2 className="w-10 h-10 text-emerald-600 mx-auto" />
                <p className="font-medium text-[#13231E]">¡Gracias! Su cita queda confirmada.</p>
              </div>
            ) : null}

            {activa && !yaPaso && !sinFecha && !hecho && !preguntaCancelar && (
              <div className="space-y-2">
                <Button
                  className="w-full h-12 text-base bg-emerald-600 hover:bg-emerald-700"
                  onClick={() => responder('confirmar')}
                  disabled={enviando}
                  data-testid="confirmar-cita-si"
                >
                  {enviando ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CheckCircle2 className="w-5 h-5 mr-2" />}
                  Confirmo que asistiré
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-11 text-red-600 border-red-200 hover:bg-red-50"
                  onClick={() => setPreguntaCancelar(true)}
                  disabled={enviando}
                  data-testid="confirmar-cita-no"
                >
                  No podré asistir
                </Button>
              </div>
            )}

            {activa && !yaPaso && !sinFecha && !hecho && preguntaCancelar && (
              <div className="space-y-2">
                <p className="text-center text-sm text-[#13231E]">
                  ¿Seguro que quiere <strong>anular</strong> la cita del {cita.fecha_texto} a las {cita.hora} h?
                </p>
                <Button
                  className="w-full h-11 bg-red-600 hover:bg-red-700"
                  onClick={() => responder('cancelar')}
                  disabled={enviando}
                  data-testid="confirmar-cita-cancelar-si"
                >
                  {enviando && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Sí, anular la cita
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-11"
                  onClick={() => setPreguntaCancelar(false)}
                  disabled={enviando}
                >
                  Volver
                </Button>
              </div>
            )}

            {cita.telefono_clinica && !yaPaso && !sinFecha && (
              <p className="text-center text-xs text-muted-foreground">
                ¿Necesita cambiar la fecha? Llámenos al {cita.telefono_clinica}.
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
